import { Badge, Box, Button, Card, Flex, Heading, Text } from '@chakra-ui/react';
import {
	ConfirmModal,
	createDataCollectionContext,
	dbTimeToUserTime,
	toaster,
	useMobileContext,
	useValidatedSorting,
	type ConfirmModalHandle,
	type DataCollectionContextProps,
	type ItemAction,
} from '@shared';
import { useEffect, useMemo, useRef, type JSX, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import surveysService from '../../api/services/surveysService';
import { createColumnHelper } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteMultipleSurveys } from '../../api/hooks/useSurvey';
import { useNavigate } from 'react-router';
import { getSurveyStatusBadgeColor, getSurveyStatusTranslation } from './utils';
import { Helmet } from 'react-helmet';

const columnHelper = createColumnHelper<Survey>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function SurveysContainer() {
	const { DataCollectionContextProvider, useDataCollection, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<Survey>(),
		[],
	);
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const { mutate: deleteMultipleSurveys } = useDeleteMultipleSurveys();

	const columns = [
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('surveyStatus', {
			header: t('general.status'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Badge colorPalette={getSurveyStatusBadgeColor(row.original.surveyStatus)}>{getSurveyStatusTranslation(row.original.surveyStatus)}</Badge>;
			},
		}),
		columnHelper.accessor('timeSubmitStart', {
			header: t('general.time_submit_start'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeSubmitStart) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeSubmitStart, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeSubmitEnd', {
			header: t('general.time_submit_end'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeSubmitEnd) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeSubmitEnd, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return dbTimeToUserTime(row.original.timeCreated, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return dbTimeToUserTime(row.original.timeModified, TIMEZONE);
			},
		}),
	];

	useValidatedSorting('surveys', columns);

	const selectedItemsActions: Array<ItemAction<Survey>> = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					surveys: ids.map(id => {
						return {
							surveyID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_surveys'),
					content: t('general.really_remove_surveys'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleSurveys(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['surveys'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	return (
		<DataCollectionContextProvider
			dataKey="surveys"
			keyAttribute="surveyID"
			emptyDataText={t('general.no_surveys_found')}
			fetchFn={(...args) => surveysService.getAllResources(...args)}
			selectedItemsActions={selectedItemsActions}
			initialItemsPerPage={25}
			columns={columns}
			defaultHiddenColumns={[]}
			isServerSide={true}
			isSelectable={true}
			defaultSort={{ id: 'timeCreated', desc: true }}
			onClickRow={row => {
				navigate(`/surveys/${row.original.surveyID}`);
			}}
		>
			<Surveys confirmRemoveRef={confirmRemoveRef} dataCollection={{ useDataCollection, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader }} />
		</DataCollectionContextProvider>
	);
}

function Surveys({
	dataCollection,
	confirmRemoveRef,
}: {
	dataCollection: {
		useDataCollection: () => DataCollectionContextProps<Survey>;
		DataCollectionTableFooter: () => JSX.Element;
		DataCollectionTableHeader: ({ isSearchShown }: { isSearchShown?: boolean }) => JSX.Element;
		DataCollectionTable: () => JSX.Element;
	};
	confirmRemoveRef: RefObject<ConfirmModalHandle | null>;
}) {
	const navigate = useNavigate();

	const { t } = useTranslation();
	const { useDataCollection, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = dataCollection;
	const { isMobile } = useMobileContext();

	const surveysCount = useDataCollection().rows.length;
	const isFetching = useDataCollection().isFetching;

	// Redirected user to survey create wizard if no surveys created
	useEffect(() => {
		if (surveysCount === 0 && !isFetching) {
			navigate('/surveys/create-survey');
		}
	}, [surveysCount, navigate, isFetching]);

	return (
		<>
			<Helmet>
				<meta name="robots" content="noindex, nofollow" />
			</Helmet>
			<Flex flexDirection="column" gap={5} width="100%" alignItems="center" height="100%">
				<Card.Root maxWidth="2000px" width="100%">
					<Card.Body>
						<Heading>{t('general.surveys')}</Heading>
						<Text>{t('general.surveys_guide')}</Text>
					</Card.Body>
				</Card.Root>
				<Card.Root maxWidth="2000px" overflow="auto" width="100%" flex={1} display="flex">
					<Card.Body colorPalette="brand.primary" display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
						<Flex gap={2} flexWrap="wrap" flexShrink={0}>
							<DataCollectionTableHeader />
							<Button
								data-cy="addSurveyButton"
								minW="175px"
								alignSelf="start"
								onClick={() => {
									navigate('/surveys/create-survey');
								}}
							>
								{t('general.add_survey')}
							</Button>
						</Flex>
						<Box flex={1} overflow="auto" minH={0}>
							<DataCollectionTable />
						</Box>
						<Flex gap={2} flexShrink={0}>
							<DataCollectionTableFooter />
							<ConfirmModal ref={confirmRemoveRef} />
						</Flex>
					</Card.Body>
				</Card.Root>
			</Flex>
		</>
	);
}
